import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, ArrowRight, BarChart3, CheckCircle2, Clock,
  Download, Eye, Mail, RefreshCw, Upload, UserPlus, Users, XCircle, Facebook, Instagram, Globe,
} from "@/lib/icons";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, formatError } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Avatar } from "@/components/PhotoUpload";
import { toast } from "sonner";
import MemberDetailSheet from "./MemberDetailSheet";

const number = (value) => Number(value || 0).toLocaleString("en-IN");
const shortDate = (value) => value ? new Intl.DateTimeFormat("en-IN", {
  day: "numeric", month: "short",
}).format(new Date(value)) : "-";

function MetricCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="admin-metric-card" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <span className="admin-metric-icon"><Icon size={16} /></span>
        {hint && <span className="text-[10px] font-medium text-white/35">{hint}</span>}
      </div>
      <div className="admin-metric-value tabular-nums">{value == null ? "-" : number(value)}</div>
      <div className="admin-metric-label">{label}</div>
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-xs text-white/40">{children}</div>;
}

function RankedBars({ items = [] }) {
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.name}>
          <div className="mb-1 flex items-center justify-between gap-3 text-xs">
            <span className="truncate text-white/70">{item.name || "(not set)"}</span>
            <span className="tabular-nums text-white/40">{number(item.count)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
            <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(4, (item.count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { theme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [ga4Overview, setGa4Overview] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const [analyticsResult, ga4Result, pendingResult, emailResult] = await Promise.allSettled([
      api.get("/admin/analytics"),
      api.get("/admin/analytics/ga4/overview", { params: { days: 30 } }),
      api.get("/admin/members", { params: { status: "pending", page: 1, limit: 5 } }),
      api.get("/admin/onboarding/analytics", { params: { days: 30 } }),
    ]);
    if (analyticsResult.status === "fulfilled") {
      setAnalytics(analyticsResult.value.data);
      setAnalyticsError(null);
    } else {
      setAnalytics({});
      setAnalyticsError(formatError(analyticsResult.reason));
    }
    setGa4Overview(ga4Result.status === "fulfilled" ? ga4Result.value.data : null);
    if (pendingResult.status === "fulfilled") {
      setPending(pendingResult.value.data.items || []);
      setPendingTotal(pendingResult.value.data.total || 0);
    } else {
      setPending([]); setPendingTotal(0);
    }
    setEmail(emailResult.status === "fulfilled" ? emailResult.value.data : null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const newMembers = useMemo(
    () => (analytics?.registrations || []).reduce((sum, item) => sum + Number(item.count || 0), 0),
    [analytics?.registrations],
  );
  const metrics = [
    { key: "members", label: "Total members", icon: Users, value: analytics?.members?.total, hint: "All time" },
    { key: "pending", label: "Pending approval", icon: Clock, value: analytics?.members?.pending, hint: "Needs attention" },
    { key: "new", label: "New members", icon: UserPlus, value: newMembers, hint: "Last 30 days" },
    { key: "pageviews", label: "Pageviews", icon: Eye, value: ga4Overview?.totals?.pageviews, hint: "GA4 · Last 30 days" },
    { key: "users", label: "Website users", icon: Activity, value: ga4Overview?.totals?.users, hint: "GA4 · Last 30 days" },
    { key: "invoices", label: "Invoice downloads", icon: Download, value: analytics?.invoice_downloads?.total, hint: "All time" },
  ];

  const memberAction = async (member, action) => {
    setActing(`${member.member_id}:${action}`);
    try {
      await api.post(`/admin/members/${encodeURIComponent(member.member_id)}/${action}`);
      toast.success(`${member.full_name} ${action === "approve" ? "approved" : "rejected"}`);
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setActing(null);
    }
  };

  const chartLine = theme === "light" ? "rgba(32,32,37,.24)" : "rgba(255,255,255,.22)";
  const chartGrid = theme === "light" ? "rgba(32,32,37,.08)" : "rgba(255,255,255,.06)";
  const emailSummary = email?.summary || {};
  const emailFailureRate = (Number(emailSummary.sent || 0) + Number(emailSummary.failed || 0))
    ? Math.round((Number(emailSummary.failed || 0) / (Number(emailSummary.sent || 0) + Number(emailSummary.failed || 0))) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <header className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Command centre</div>
          <h1 className="mt-2 font-display text-white">Good to see you.</h1>
          <p className="admin-page-description">Review what needs attention, understand community activity, and move quickly to the work that matters.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <Link to="/admin/import" className="btn-glow inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold text-white">
            <Upload size={14} /> Import members
          </Link>
        </div>
      </header>

      <section aria-label="Key metrics">
        {analyticsError && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-400/20 bg-red-400/[.07] px-4 py-3 text-xs text-red-200" role="alert">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>Dashboard totals could not be loaded. {analyticsError}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
          {metrics.map(({ key, ...metric }) => <MetricCard key={key} {...metric} testid={`admin-stat-${key}`} />)}
        </div>
      </section>

      <div className="grid items-start gap-4 xl:grid-cols-[1.25fr_.75fr]">
        <section className="admin-surface overflow-hidden" data-testid="dashboard-pending-members">
          <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
            <div>
              <h2 className="admin-section-heading">Pending member reviews</h2>
              <p className="admin-section-copy">Approve complete profiles or reject invalid submissions.</p>
            </div>
            <Link to="/admin/members" className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300">View all <ArrowRight size={13} /></Link>
          </div>
          <div className="divide-y divide-white/5">
            {pending.map((member) => (
              <div key={member.member_id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar src={member.photo_url} name={member.full_name} avatarKey={member.avatar_key} size={40} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{member.full_name}</div>
                    <div className="mt-0.5 truncate text-[11px] text-white/40">{member.member_id} · {[member.city, member.state].filter(Boolean).join(", ") || "Location missing"}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <SocialLink href={member.website} label="Website" icon={Globe} />
                      <SocialLink href={member.facebook_url} label="Facebook" icon={Facebook} />
                      <SocialLink href={member.instagram_url} label="Instagram" icon={Instagram} />
                      {!member.website && !member.facebook_url && !member.instagram_url && <span className="text-[10px] text-amber-300/70">Social links missing</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pl-[52px] sm:pl-0">
                  <button type="button" onClick={() => setReviewFor(member.member_id)} disabled={!!acting}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-[11px] font-semibold text-white/70 hover:bg-white/5 disabled:opacity-40">
                    <Eye size={13} /> Review
                  </button>
                  <button type="button" onClick={() => memberAction(member, "reject")} disabled={!!acting}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/20 px-3 py-2 text-[11px] font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-40">
                    <XCircle size={13} /> Reject
                  </button>
                  <button type="button" onClick={() => memberAction(member, "approve")} disabled={!!acting}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
                    <CheckCircle2 size={13} /> Approve
                  </button>
                </div>
              </div>
            ))}
            {!loading && !pending.length && <div className="p-5"><EmptyState>No members are waiting for review.</EmptyState></div>}
            {loading && !pending.length && <div className="p-8 text-center text-xs text-white/40">Loading member queue…</div>}
          </div>
          {pendingTotal > pending.length && <div className="border-t border-white/5 px-5 py-3 text-[11px] text-white/40">Showing 5 of {number(pendingTotal)} pending members</div>}
        </section>

        <section className="admin-surface p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="admin-section-heading">Operations health</h2>
              <p className="admin-section-copy">Email automation and member workflow signals.</p>
            </div>
            <Mail size={18} className="text-violet-300" />
          </div>
          <div className="mt-5 space-y-3">
            <Link to="/admin/email" className="flex items-center justify-between gap-4 rounded-xl border border-white/8 bg-white/[.025] p-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`h-2 w-2 rounded-full ${email?.paused ? "bg-amber-400" : "bg-emerald-400"}`} />
                <div><div className="text-xs font-semibold text-white">Automatic onboarding</div><div className="mt-0.5 text-[10px] text-white/40">{email?.paused ? "Paused" : "Running normally"}</div></div>
              </div>
              <ArrowRight size={14} className="text-white/30" />
            </Link>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/8 bg-white/[.025] p-3.5">
                <div className="text-[10px] font-medium text-white/40">Email failures · 30d</div>
                <div className="mt-2 font-display text-2xl text-white">{number(emailSummary.failed)}</div>
                <div className={`mt-1 text-[10px] ${emailFailureRate > 5 ? "text-red-300" : "text-emerald-300"}`}>{emailFailureRate}% failure rate</div>
              </div>
              <div className="rounded-xl border border-white/8 bg-white/[.025] p-3.5">
                <div className="text-[10px] font-medium text-white/40">Profiles completed</div>
                <div className="mt-2 font-display text-2xl text-white">{number(emailSummary.profile_completed)}</div>
                <div className="mt-1 text-[10px] text-white/35">After onboarding</div>
              </div>
            </div>
            {emailFailureRate > 5 && (
              <Link to="/admin/email" className="flex items-start gap-2 rounded-xl border border-amber-400/20 bg-amber-400/8 p-3 text-[11px] leading-5 text-amber-200">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> Email failures need attention. Open Email to review the delivery history.
              </Link>
            )}
          </div>
        </section>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[1.25fr_.75fr]">
        <section className="admin-surface p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="admin-section-heading">Community growth</h2><p className="admin-section-copy">New member registrations over the last 30 days.</p></div>
            <Link to="/admin/analytics" className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300"><BarChart3 size={14} /> Full analytics</Link>
          </div>
          <div className="mt-5 h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.registrations || []} margin={{ left: -20, right: 8 }}>
                <defs><linearGradient id="dashboardGrowth" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8b19ff" stopOpacity={0.32} /><stop offset="100%" stopColor="#8b19ff" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke={chartGrid} vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} stroke={chartLine} fontSize={10} tickLine={false} minTickGap={30} />
                <YAxis allowDecimals={false} stroke={chartLine} fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: theme === "light" ? "#fff" : "#191920", border: `1px solid ${chartGrid}`, borderRadius: 10, fontSize: 11 }} labelFormatter={shortDate} />
                <Area type="monotone" dataKey="count" name="New members" stroke="#8b19ff" strokeWidth={2} fill="url(#dashboardGrowth)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="admin-surface overflow-hidden">
          <div className="border-b border-white/10 px-5 py-4">
            <h2 className="admin-section-heading">Recent member activity</h2>
            <p className="admin-section-copy">Latest profile and status updates.</p>
          </div>
          <div className="divide-y divide-white/5">
            {(analytics?.recent_activity || []).slice(0, 6).map((item) => (
              <Link key={item.member_id} to={`/admin/members?member=${encodeURIComponent(item.member_id || "")}`} className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-white/[.025]">
                <div className="min-w-0"><div className="truncate text-xs font-semibold text-white/80">{item.full_name}</div><div className="mt-1 text-[10px] text-white/35">{item.member_id} · {item.status}</div></div>
                <span className="shrink-0 text-[10px] text-white/35">{shortDate(item.updated_at)}</span>
              </Link>
            ))}
            {!analytics?.recent_activity?.length && <div className="p-5"><EmptyState>Recent member activity will appear here.</EmptyState></div>}
          </div>
        </section>
      </div>

      <div className="grid items-start gap-4 md:grid-cols-2">
        <section className="admin-surface p-5">
          <h2 className="admin-section-heading">Members by state</h2>
          <p className="admin-section-copy">Approved members, top 8 states.</p>
          <div className="mt-5">
            {analytics?.members_by_state?.length
              ? <RankedBars items={analytics.members_by_state} />
              : <EmptyState>State breakdown will appear here.</EmptyState>}
          </div>
        </section>
        <section className="admin-surface p-5">
          <h2 className="admin-section-heading">Members by district</h2>
          <p className="admin-section-copy">Approved members, top 8 districts.</p>
          <div className="mt-5">
            {analytics?.members_by_district?.length
              ? <RankedBars items={analytics.members_by_district} />
              : <EmptyState>District breakdown will appear here.</EmptyState>}
          </div>
        </section>
      </div>
      <MemberDetailSheet memberId={reviewFor} open={!!reviewFor}
        onOpenChange={(open) => { if (!open) setReviewFor(null); }} onSaved={load} />
    </div>
  );
}

function SocialLink({ href, label, icon: Icon }) {
  if (!href) return null;
  const url = href.startsWith("http") ? href : `https://${href}`;
  return <a href={url} target="_blank" rel="noreferrer" title={label}
    className="rounded-md border border-white/10 bg-white/[.035] p-1.5 text-violet-300 hover:bg-violet-500/10"><Icon size={11} /></a>;
}
